import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { CurrentUser } from "@/features/user/types";
import { queries } from "@/utils/query";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  AudioLinesIcon,
  ChevronRightIcon,
  LogOutIcon,
  SettingsIcon,
  UploadIcon,
  UserRoundIcon,
} from "lucide-react";
import { Suspense } from "react";
import toast from "react-hot-toast";
import { Image } from "../ui/image";
import { NotificationDropdown } from "./notification-dropdown";

export function UserActions({ currentUser }: { currentUser: CurrentUser }) {
  const navigate = useNavigate();

  const logoutMutation = queries.auth.logout.useMutation({
    onSuccess: () => {
      toast.success("退出登录成功");
      navigate({ to: "/" });
    },
  });

  return (
    <Suspense fallback="Loading...">
      <Button intent="solid" size="md" asChild>
        <Link to="/artwork/create">
          <UploadIcon />
          发布
        </Link>
      </Button>

      <NotificationDropdown className="ml-3 mr-7" />

      <div className="flex items-center shrink-0">
        <DropdownMenu triggerEvent="hover" modal={false}>
          <DropdownMenuTrigger asChild>
            <Button intent="plain" size="icon" asChild>
              <Link
                to="/user/$username/gallery"
                params={{ username: currentUser.userId }}
              >
                <Image
                  alt={currentUser.username}
                  src={currentUser.avatarUrl}
                  className="block border rounded-full size-10 bg-gray-subtle-bg"
                  width={40}
                  aspectRatio={1}
                />
              </Link>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            sideOffset={16}
            align="end"
            className="relative w-68"
          >
            <div className="absolute top-0 left-0 w-full aspect-toutu -z-10">
              <img
                src="https://picsum.photos/800/450"
                alt=""
                className="object-cover size-full"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-subtle-bg to-gray-subtle-bg/20" />
            </div>

            <div className="flex flex-col items-center my-2">
              <div className="relative size-16">
                <Image
                  alt=""
                  src={currentUser.avatarUrl}
                  className="size-full bg-gray-solid"
                  width={64}
                  aspectRatio={1}
                />
                {/* 虽然是废稿，但我觉得可以用来放称号什么的，先保留 */}
                {/* <Badge
                  variant="default"
                  size="xs"
                  className="absolute inset-x-0 bottom-0 mx-auto translate-y-1/2 w-fit"
                >
                  Lv. 3
                </Badge> */}
              </div>

              <p className="mt-3.5 text-gray-fg-primary">
                {currentUser.username}
              </p>

              <Badge variant="default" size="xs" className="mt-1">
                Lv. 3
              </Badge>
            </div>

            <div className="flex justify-center my-2 space-x-10">
              <Link
                className="flex flex-col items-center"
                to="/user/$username/following"
                params={{ username: currentUser.userId }}
              >
                <span className="text-lg text-gray-fg-primary">15</span>
                <span className="text-xs text-gray-fg-secondary">关注</span>
              </Link>
              <Link
                className="flex flex-col items-center"
                to="/user/$username/followers"
                params={{ username: currentUser.userId }}
              >
                <span className="text-lg text-gray-fg-primary">122</span>
                <span className="text-xs text-gray-fg-secondary">粉丝数</span>
              </Link>
              <Link
                className="flex flex-col items-center"
                to="/user/$username/gallery"
                params={{ username: currentUser.userId }}
              >
                <span className="text-lg text-gray-fg-primary">8</span>
                <span className="text-xs text-gray-fg-secondary">作品</span>
              </Link>
            </div>

            <div className="h-4" />

            <DropdownMenuGroup>
              <DropdownMenuItem asChild>
                <Link to="/user/settings">
                  <SettingsIcon />
                  个人中心
                  <DropdownMenuShortcut>
                    <ChevronRightIcon />
                  </DropdownMenuShortcut>
                </Link>
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem asChild>
                <Link
                  to="/user/$username/gallery"
                  params={{ username: currentUser.userId }}
                >
                  <UserRoundIcon />
                  我的主页
                  <DropdownMenuShortcut>
                    <ChevronRightIcon />
                  </DropdownMenuShortcut>
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/">
                  <AudioLinesIcon />
                  我的圈子
                  <DropdownMenuShortcut>
                    <ChevronRightIcon />
                  </DropdownMenuShortcut>
                </Link>
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem
                className="cursor-pointer"
                onSelect={() => {
                  logoutMutation.mutate(undefined);
                }}
              >
                <LogOutIcon />
                退出登录
              </DropdownMenuItem>
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </Suspense>
  );
}
